import { PublicClientApplication, AccountInfo, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { Authenticator, LoginResult, User, UserNameType } from './authenticator';

export interface AzureB2CConfig {
  clientId: string;
  tenantName: string;
  policies: string[];
  redirectUri: string;
  logoutRedirectUri?: string;
  scopes: string[] | string;
}

let globalAzureB2CConfig: AzureB2CConfig | undefined;
const ACTIVE_POLICY_STORAGE_KEY = 'azureB2CActivePolicy';
const CURRENT_LOGIN_POLICY_KEY = 'azureB2CCurrentLoginPolicy';
const AADB2C_SERVER_ERROR_CODE = 'AADB2C90075';

export function configureAzureB2C(config: AzureB2CConfig) {
  globalAzureB2CConfig = {
    ...config,
    scopes: normalizeScopes(config.scopes)
  };
}

export class AzureB2CAuthenticator extends Authenticator {
  private readonly pca: PublicClientApplication;
  private readonly scopes: string[];
  private readonly initialized: Promise<void>;
  private readonly policies: string[];
  private readonly primaryPolicy: string;
  private readonly tenantName: string;

  constructor(config?: AzureB2CConfig) {
    super();
    const effectiveConfig: AzureB2CConfig | undefined = config ?? globalAzureB2CConfig;
    if (!effectiveConfig) {
      throw new Error('Azure B2C configuration missing');
    }

    this.policies = effectiveConfig.policies;
    this.tenantName = effectiveConfig.tenantName;
    this.primaryPolicy = this.policies[0];
    const authority = this.buildAuthority(this.primaryPolicy);

    this.pca = new PublicClientApplication({
      auth: {
        clientId: effectiveConfig.clientId,
        authority: authority,
        redirectUri: effectiveConfig.redirectUri,
        postLogoutRedirectUri: effectiveConfig.logoutRedirectUri ?? effectiveConfig.redirectUri,
        navigateToLoginRequestUrl: false,
        knownAuthorities: [`${effectiveConfig.tenantName}.b2clogin.com`]
      },
      cache: {
        cacheLocation: 'localStorage'
      }
    });
    this.scopes = ensureAuthScopes(normalizeScopes(effectiveConfig.scopes));

    this.initialized = this.pca
      .initialize()
      .then(async () => {
        await this.processRedirectWithRetry();
      })
      .catch(() => undefined);
  }

  private async ensureInitialized(): Promise<void> {
    await this.initialized;
  }

  private async processRedirectWithRetry(): Promise<void> {
    // Check for errors in URL first
    const errorInfo = this.extractErrorFromUrl();
    if (errorInfo) {
      console.error('[Azure B2C] Error in redirect URL:', errorInfo);
      // Clear error from URL to prevent retry loops
      this.clearErrorFromUrl();
      // For certain errors, we might want to continue instead of throwing
      // AADB2C90075 is a server error that might be transient
      if (errorInfo.errorCode === AADB2C_SERVER_ERROR_CODE) {
        console.warn('[Azure B2C] Server error detected, clearing state and continuing');
        return;
      }
      throw new Error(`Azure B2C error: ${errorInfo.errorCode} - ${errorInfo.errorDescription || errorInfo.error}`);
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await this.pca.handleRedirectPromise();
        if (result?.account) {
          this.setActiveAccount(result.account);
          return;
        }

        const accounts = this.pca.getAllAccounts();
        if (accounts.length > 0) {
          this.setActiveAccount(accounts[0]);
          return;
        }

        if (!window.location.hash.includes('code=') && !window.location.search.includes('code=')) {
          return;
        }

        await new Promise((r) => setTimeout(r, attempt * 200));
      } catch (error: any) {
        const errorMessage = error?.message || String(error);
        const errorCode = this.extractErrorCode(errorMessage);

        console.error(`[Azure B2C] Error processing redirect (attempt ${attempt}/${maxAttempts}):`, error);

        // Handle specific error codes
        if (errorCode === AADB2C_SERVER_ERROR_CODE) {
          // Server error - might be transient, try to recover
          if (attempt < maxAttempts) {
            console.warn('[Azure B2C] Server error, retrying...');
            await new Promise((r) => setTimeout(r, attempt * 500));
            continue;
          }
          console.error('[Azure B2C] Server error persisted after retries, clearing error state');
          this.clearErrorFromUrl();
          return;
        }

        if (attempt === maxAttempts) {
          throw error;
        }

        // Wait before retry
        await new Promise((r) => setTimeout(r, attempt * 200));
      }
    }
  }

  private extractErrorFromUrl(): { error: string; errorDescription?: string; errorCode?: string } | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const hash = window.location.hash;
    const search = window.location.search;
    const urlParams = new URLSearchParams(hash.substring(1) + (search ? '&' + search.substring(1) : ''));

    const error = urlParams.get('error');
    if (!error) {
      return null;
    }

    const errorDescription = urlParams.get('error_description') || undefined;
    return {
      error,
      errorDescription: errorDescription || undefined,
      errorCode: errorDescription ? this.extractErrorCode(errorDescription) : undefined
    };
  }

  private extractErrorCode(errorMessage: string): string | null {
    // Extract error codes like AADB2C90075 from error messages
    const match = errorMessage.match(/AADB2C\d+/);
    return match ? match[0] : null;
  }

  private clearErrorFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('error_code');
      url.searchParams.delete('correlation_id');

      // Use replaceState to avoid adding to history
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('[Azure B2C] Failed to clear error from URL:', e);
    }
  }

  private getActiveAccount(): AccountInfo | null {
    const activeAccount = this.pca.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.pca.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    const rememberedPolicy = this.getRememberedPolicy();
    if (rememberedPolicy) {
      const accountForPolicy = accounts.find((account) => {
        const policyFromAccount = this.extractPolicyFromAccount(account);
        return policyFromAccount?.toLowerCase() === rememberedPolicy.toLowerCase();
      });
      if (accountForPolicy) {
        this.setActiveAccount(accountForPolicy);
        return accountForPolicy;
      }
    }

    if (accounts.length > 1) {
      console.warn('[Azure B2C] Multiple accounts detected, clearing cache');
      accounts.forEach((acc) => {
        this.pca.clearCache({ account: acc });
      });
      return null;
    }

    this.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  private setActiveAccount(account: AccountInfo): void {
    this.pca.setActiveAccount(account);
    const policy = this.getPolicyForAccount(account);
    this.rememberActivePolicy(policy);
  }

  private buildAuthority(policy: string): string {
    return `https://${this.tenantName}.b2clogin.com/${this.tenantName}.onmicrosoft.com/${policy}`;
  }

  private extractPolicyFromAccount(account: AccountInfo): string | null {
    const claims = (account.idTokenClaims ?? {}) as { tfp?: string; acr?: string };
    return claims.tfp || claims.acr || null;
  }

  private getPolicyForAccount(account: AccountInfo): string {
    const policyFromClaims = this.extractPolicyFromAccount(account);

    if (policyFromClaims) {
      const matchingPolicy = this.policies.find((p) => p.toLowerCase() === policyFromClaims.toLowerCase());
      if (matchingPolicy) {
        return matchingPolicy;
      }
      console.warn('[Azure B2C] Policy not recognized:', policyFromClaims, 'using primary:', this.primaryPolicy);
    }

    return this.primaryPolicy;
  }

  private rememberActivePolicy(policy: string): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(ACTIVE_POLICY_STORAGE_KEY, policy);
      }
    } catch {
      // Ignore storage errors
    }
  }

  private getRememberedPolicy(): string | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(ACTIVE_POLICY_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
    return null;
  }

  private async acquireToken(): Promise<string | null> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    const policyForAccount = this.getPolicyForAccount(account);
    const authority = this.buildAuthority(policyForAccount);

    try {
      const result = await this.pca.acquireTokenSilent({
        account,
        scopes: this.scopes,
        authority
      });

      if (!result.accessToken && result.idToken) {
        return result.idToken;
      }

      return result.accessToken;
    } catch (e) {
      console.error('[Azure B2C] Token acquisition failed:', e);
      return account.idToken || null;
    }
  }

  async getUser(): Promise<User> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    const accessToken = (await this.acquireToken()) || '';
    const claims = account?.idTokenClaims as any;

    const email = claims?.emails?.[0] || claims?.email || claims?.signInNames?.emailAddress || account?.username || '';
    const name = claims?.name || '';
    const givenName = claims?.given_name || '';
    const familyName = claims?.family_name || '';
    const fullName = name || [givenName, familyName].filter(Boolean).join(' ') || email;
    const currentPolicy = account ? this.getPolicyForAccount(account) : '';

    return {
      email,
      fullName,
      authorizationHeader: accessToken ? `Bearer ${accessToken}` : '',
      metadata: {
        azureB2CPolicy: currentPolicy
      }
    };
  }

  async getAuthenticationHeader(): Promise<string> {
    const user = await this.getUser();
    return user.authorizationHeader;
  }

  async getCurrentPolicy(): Promise<string> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    return account ? this.getPolicyForAccount(account) : this.primaryPolicy;
  }

  async getAuthenticationHeaders(): Promise<Record<string, string>> {
    const user = await this.getUser();
    const policy = await this.getCurrentPolicy();

    return {
      Authorization: user.authorizationHeader,
      'X-Azure-B2C-Policy': policy
    };
  }

  async isAuthenticated(): Promise<boolean> {
    await this.ensureInitialized();
    return !!this.getActiveAccount();
  }

  async login(policy?: string, __?: string): Promise<LoginResult> {
    await this.ensureInitialized();
    const targetPolicy = policy || this.primaryPolicy;

    if (!this.policies.includes(targetPolicy)) {
      throw new Error(`Policy '${targetPolicy}' not found in configuration`);
    }

    await this.pca.loginRedirect({
      scopes: this.scopes,
      authority: this.buildAuthority(targetPolicy)
    });

    return { isLoggedIn: false, totpSetupDetails: { authenticatorCode: '', qrCodeUrl: '' } };
  }

  getAvailablePolicies(): string[] {
    return [...this.policies];
  }

  static getPolicyDisplayName(policy: string): string {
    return policy.replace(/^B2C_1[A-Z]?_/i, '').replace(/_/g, ' ');
  }

  userNameType(): UserNameType {
    return UserNameType.EMAIL;
  }

  requiresCredentials(): boolean {
    return false;
  }

  enableForgotPassword(): boolean {
    return false;
  }

  async forgotPassword(_username: string): Promise<any> {
    return Promise.reject(new Error('Not implemented'));
  }

  async forgotPasswordConfirm(_username: string, _password: string, _code: string): Promise<any> {
    return Promise.reject(new Error('Not implemented'));
  }

  async logout(): Promise<void> {
    await this.ensureInitialized();
    const account = this.getActiveAccount();
    await this.pca.logoutRedirect({ account: account ?? undefined });
  }
}

function normalizeScopes(input?: string[] | string): string[] {
  if (!input) {
    return [];
  }
  if (Array.isArray(input)) {
    return input.map((s) => s.trim()).filter(Boolean);
  }
  return input
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ensureAuthScopes(scopes: string[]): string[] {
  const set = new Set(scopes);
  set.add('openid');
  set.add('profile');
  return Array.from(set);
}
